import React, { Children, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { ArrowDropDown, ArrowRight } from '@mui/icons-material'
import { Box, Button, Pagination, Tab, Tabs, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { CUSTOMER_DETAILS_DEFAULT_PAGINATION } from 'settings/constants/pagination'
import { INVOICE_GENERATION_STATUS, INVOICE_STATUS } from 'settings/constants/billing'
import { useLazyGetAccountInvoicesQuery } from 'api/accounts/getAccountInvoices'
import { useLazyGetEmailEventsQuery } from 'api/accounts/getEmailEvents'
import {
  CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_INVOICE_LIST,
  CACHE_TAG_WORK_ORDER_LIST,
} from 'api/cacheTagTypes'
import { getInvoicesByStatus, getInvoicesFromEmailEvents } from 'data/invoice/invoiceSelectors'
import { useLazyGetARCustomerSummaryQuery } from 'api/billing/getARCustomerSummary'
import { PAYMENT_METHODS } from 'settings/constants/payment'
import { AGING_BUCKET_LABEL, AGING_BUCKET_NAME } from 'settings/constants/accountReceivables'
import { setInvoicesPaymentsState } from 'slices/billing/invoicesSlice'

import T from 'T'
import api from 'api'
import HHTablePagination from 'components/common/HHTablePagination'
import InvoiceActionMenu from 'components/billing/common/InvoiceActionMenu'
import InvoiceDrawer from 'components/billing/invoices/InvoiceDrawer'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import AgingBucket from 'components/common/aging-bucket/AgingBucket'

import noop from 'lodash/noop'
import InteractiveAgingBucket from 'components/common/aging-bucket/InteractiveAgingBucket/InteractiveAgingBucket'
import AgingBucketDialog from 'components/common/aging-bucket/InteractiveAgingBucket/AgingBucketDialog'
import { Accordion, AccordionDetails, AccordionSummary } from './StyledAccordian'
import InvoiceAccordion from '../content/invoices/InvoiceAccordion'
import InvoiceEdit from '../../billing/invoices/invoice-edit'
import { CommonDrawer } from '../../common'
import CreateInvoice from '../content/CreateInvoice'
import TakePaymentsDialog from '../../billing/groups/payment/take-payments'

const { PARTIAL, UNPAID, PAID } = INVOICE_STATUS
const { ACCOUNT_CREDIT } = PAYMENT_METHODS
const { INITIAL_PAGE, ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } = CUSTOMER_DETAILS_DEFAULT_PAGINATION
const { CURRENT, DUE_ONE_THIRTY, DUE_THIRTY_ONE_SIXTY, DUE_SIXTY_ONE_NINETY, DUE_NINETY_ONE_ONE_TWENTY, DUE_OVER_ONE_TWENTY } =
  AGING_BUCKET_NAME

const isNonTerminalGenerationStatus = inv =>
  inv.latestBatchStatus === INVOICE_GENERATION_STATUS.Initialized ||
  inv.latestBatchStatus === INVOICE_GENERATION_STATUS.Started ||
  inv.latestBatchStatus === INVOICE_GENERATION_STATUS.Regeneration_Started

const Invoices = ({
  accountId,
  customerDetails = null,
  initialTab = PARTIAL,
  showCreateInvoiceButton = false,
  showARData = false,
  isViewOnly = false,
}) => {
  const dispatch = useDispatch()
  const [getARCustomerSummary, { data: summaryData }] = useLazyGetARCustomerSummaryQuery()
  const {
    currentCents = 0,
    dueOneThirtyCents = 0,
    dueThirtyOneSixtyCents = 0,
    dueSixtyOneNinetyCents = 0,
    dueNinetyOneOneTwentyCents = 0,
    dueOverOneTwentyCents = 0,
  } = summaryData || {}
  const customerCreditBalanceCents = get(customerDetails, 'customerCreditBalanceCents', 0)
  const outstandingAmount =
    currentCents + dueOneThirtyCents + dueThirtyOneSixtyCents + dueSixtyOneNinetyCents + dueNinetyOneOneTwentyCents + dueOverOneTwentyCents

  const [isOpenEditInvoiceWOListing, setIsOpenEditInvoiceWOListing] = useState()
  const [isOpenCreateInvoice, setIsOpenCreateInvoice] = useState(false)
  const [getAccountInvoices, { data: invoiceList, isFetching, isLoading }] = useLazyGetAccountInvoicesQuery()
  const [getEmailEvents, { data: emailEventList }] = useLazyGetEmailEventsQuery()
  const [isInvoicesExpanded, setIsInvoicesExpanded] = useState(true)
  const [selectedTab, setSelectedTab] = useState(initialTab)
  const [pagination, setPagination] = useState({ page: INITIAL_PAGE, rowsPerPage: ROWS_PER_PAGE })
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [isOpenInvoicePDF, setIsOpenInvoicePDF] = useState(false)
  const [isPollingActive, setIsPollingActive] = useState(false)
  const [pollingExhausted, setPollingExhausted] = useState(false)
  const pollCountRef = useRef(0)
  const transactionTimeoutRef = useRef(null)
  const { page, rowsPerPage } = pagination
  const shimmersArray = new Array(1).fill(0)

  // Redux selectors for TakePaymentsDialog
  const isOpenTakePaymentsDialog = useSelector(state => state.invoices.payments.isOpenTakePaymentsDialog)
  const selectedRow = useSelector(state => state.invoices.payments.selectedRow, shallowEqual)
  const selectedInvoiceNumber = get(selectedRow, 'invoiceNumber')

  // External regenerate signal (see the nonce-watching effect below). Seed the ref with the value at
  // mount so a stale signal from a previous visit doesn't fire polling on load.
  const draftRegenerationPolling = useSelector(state => state.invoices.draftRegenerationPolling, shallowEqual)
  const lastRegenerationNonceRef = useRef(draftRegenerationPolling?.nonce ?? 0)

  const refreshInvoiceList = () => {
    if (!accountId) return
    pollCountRef.current = 0
    setPollingExhausted(false)
    dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId }]))
  }

  const invalidateAccountTransaction = () => {
    pollCountRef.current = 0
    if (transactionTimeoutRef.current !== null) {
      clearTimeout(transactionTimeoutRef.current)
    }
    transactionTimeoutRef.current = setTimeout(() => {
      transactionTimeoutRef.current = null
      dispatch(
        api.util.invalidateTags([
          CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
          CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
          CACHE_TAG_ACCOUNT_INVOICE_LIST,
          CACHE_TAG_INVOICE_LIST,
          CACHE_TAG_WORK_ORDER_LIST,
          CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
        ])
      )
      setIsPollingActive(true)
    }, 4000)
  }

  const handleInvoicesAccChange = () => setIsInvoicesExpanded(!isInvoicesExpanded)

  const handlePageChange = (newPage = INITIAL_PAGE) => setPagination(p => ({ ...p, page: newPage }))

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
    handlePageChange()
  }

  const handleOpenActionMenu = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleCloseActionMenu = () => {
    setAnchorEl(null)
  }

  const handleInvoicePDF = invoice => {
    setSelectedInvoice(invoice)
    setIsOpenInvoicePDF(true)
  }

  const handleEditInvoice = () => {
    setIsOpenInvoicePDF(false)
    setIsOpenEditInvoiceWOListing(!isOpenEditInvoiceWOListing)
  }

  const handleCloseInvoiceDrawer = () => {
    setIsOpenInvoicePDF(false)
  }

  const handlePaymentsDrawerClose = () => dispatch(setInvoicesPaymentsState({ isOpenTakePaymentsDialog: false, selectedRow: undefined }))

  const invoicesByStatus = getInvoicesByStatus({ invoices: get(invoiceList, 'invoices', []) })
  const invoicesFromEmailEvents = getInvoicesFromEmailEvents({ emailEvents: get(emailEventList, 'emailEvents', []) })

  const currentTabInvoices = get(invoicesByStatus, selectedTab, [])
  const currentInvoices = isLoading ? shimmersArray : currentTabInvoices.slice(page * rowsPerPage, (page + 1) * rowsPerPage)

  useEffect(() => {
    if (transactionTimeoutRef.current !== null) {
      clearTimeout(transactionTimeoutRef.current)
      transactionTimeoutRef.current = null
    }
    if (accountId) {
      pollCountRef.current = 0
      setIsPollingActive(false)
      setPollingExhausted(false)
      getAccountInvoices({ accountId }).unwrap().catch(handleError)
      getEmailEvents({ accountId }).unwrap().catch(handleError)
      if (showARData) {
        getARCustomerSummary({ accountId })
      }
    }
    return () => {
      if (transactionTimeoutRef.current !== null) {
        clearTimeout(transactionTimeoutRef.current)
      }
    }
  }, [accountId])

  useEffect(() => {
    if (currentTabInvoices?.length && !currentInvoices?.length) {
      handlePageChange()
    }
  }, [currentInvoices])

  useEffect(() => {
    if (!isPollingActive || isFetching || !accountId) return noop
    const invoices = get(invoiceList, 'invoices', [])
    const hasNonTerminal = invoices.some(isNonTerminalGenerationStatus)
    const MAX_POLLS = 5
    const MIN_POLLS_BEFORE_STOP = 2 // Buffer for async pubsub delivery
    if (pollCountRef.current >= MAX_POLLS || (!hasNonTerminal && pollCountRef.current >= MIN_POLLS_BEFORE_STOP)) {
      if (pollCountRef.current >= MAX_POLLS && hasNonTerminal) {
        setPollingExhausted(true)
      }
      setIsPollingActive(false)
      return noop
    }
    pollCountRef.current += 1
    const timer = setTimeout(() => {
      dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId }]))
    }, 3000)
    return () => clearTimeout(timer)
  }, [isPollingActive, invoiceList, isFetching, accountId, dispatch])

  const activatePolling = useCallback(() => {
    pollCountRef.current = 0
    setPollingExhausted(false)
    setIsPollingActive(true)
  }, [])

  // Re-arm polling when an external regenerate signals this account (e.g. the payment-terms /
  // billing-profile "regenerate drafts" prompt, which lives outside this subtree). Mirrors the manual
  // regenerate flow's activatePolling — resetting any prior exhausted cycle — so the per-invoice
  // progress bar + auto-refresh appear on every regeneration. The ref ignores the value present at
  // mount, acting only on subsequent bumps for this account.
  useEffect(() => {
    const nonce = draftRegenerationPolling?.nonce ?? 0
    if (nonce === lastRegenerationNonceRef.current) return
    lastRegenerationNonceRef.current = nonce
    if (draftRegenerationPolling?.accountId === accountId) activatePolling()
  }, [draftRegenerationPolling, accountId, activatePolling])

  return (
    <>
      <Accordion expanded={isInvoicesExpanded}>
        <AccordionSummary
          withNewColors
          sx={{
            '& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content': {
              mb: 0,
            },
          }}
          disablePointer
          expandIcon={undefined}
          iconGap={false}
        >
          <Grid container alignItems="center" sx={{ width: '100%' }}>
            <Grid display="flex">
              {isInvoicesExpanded ? (
                <ArrowDropDown cursor="pointer" onClick={handleInvoicesAccChange} color="disabled" />
              ) : (
                <ArrowRight cursor="pointer" onClick={handleInvoicesAccChange} color="disabled" />
              )}
            </Grid>

            <Grid size="grow">
              <Typography
                sx={{ cursor: 'pointer' }}
                variant="h6"
                fontWeight={500}
                margin="auto 20px auto 8px"
                onClick={handleInvoicesAccChange}
              >
                {T.INVOICES}
              </Typography>
            </Grid>
            {showCreateInvoiceButton && (
              <Grid display="flex" justifyContent="flex-end" size="grow">
                <Button size="small" variant="outlined" onClick={() => setIsOpenCreateInvoice(true)}>
                  {T.CREATE_INVOICE}
                </Button>
              </Grid>
            )}
            {showARData && (
              <Box my={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                <InteractiveAgingBucket accountId={accountId} type={CURRENT} label={AGING_BUCKET_LABEL[CURRENT]} value={currentCents} />
                <InteractiveAgingBucket
                  accountId={accountId}
                  type={DUE_ONE_THIRTY}
                  label={AGING_BUCKET_LABEL[DUE_ONE_THIRTY]}
                  value={dueOneThirtyCents}
                />
                <InteractiveAgingBucket
                  accountId={accountId}
                  type={DUE_THIRTY_ONE_SIXTY}
                  label={AGING_BUCKET_LABEL[DUE_THIRTY_ONE_SIXTY]}
                  value={dueThirtyOneSixtyCents}
                />
                <InteractiveAgingBucket
                  accountId={accountId}
                  type={DUE_SIXTY_ONE_NINETY}
                  label={AGING_BUCKET_LABEL[DUE_SIXTY_ONE_NINETY]}
                  value={dueSixtyOneNinetyCents}
                />
                <InteractiveAgingBucket
                  accountId={accountId}
                  type={DUE_NINETY_ONE_ONE_TWENTY}
                  label={AGING_BUCKET_LABEL[DUE_NINETY_ONE_ONE_TWENTY]}
                  value={dueNinetyOneOneTwentyCents}
                />
                <InteractiveAgingBucket
                  accountId={accountId}
                  type={DUE_OVER_ONE_TWENTY}
                  label={AGING_BUCKET_LABEL[DUE_OVER_ONE_TWENTY]}
                  value={dueOverOneTwentyCents}
                />
                <AgingBucket type={ACCOUNT_CREDIT} label="Account credit" value={customerCreditBalanceCents} />
                <AgingBucket label={T.OUTSTANDING} value={outstandingAmount} />
                <AgingBucketDialog />
              </Box>
            )}

            <Grid size={12}>
              <Tabs value={selectedTab} onChange={handleTabChange} sx={{ button: { textTransform: 'capitalize' } }}>
                <Tab value={PARTIAL} label={`${T.DRAFT}s`} />
                <Tab value={UNPAID} label={T.UNPAID} />
                <Tab value={PAID} label={T.PAID} />
              </Tabs>
            </Grid>
          </Grid>
        </AccordionSummary>

        <AccordionDetails>
          <Box width="100%">
            <Box borderTop="1px solid" borderBottom="1px solid" borderColor="border.light">
              {Children.toArray(
                currentInvoices.map(invoice => (
                  <InvoiceAccordion
                    isViewOnly={isViewOnly}
                    isLoading={isLoading}
                    selectedTab={selectedTab}
                    invoice={invoice}
                    emailEvents={invoicesFromEmailEvents.filter(event => event?.invoiceNumber === invoice?.invoiceNumber)}
                    onClick={() => handleInvoicePDF(invoice)}
                    onActionClick={event => {
                      handleOpenActionMenu(event)
                      setSelectedInvoice(invoice)
                    }}
                    pollingExhausted={pollingExhausted}
                    isPollingActive={isPollingActive}
                  />
                ))
              )}
            </Box>

            {currentTabInvoices.length > 0 && (
              <Box backgroundColor="background.header" px={3} display="flex" alignItems="center" justifyContent="space-between">
                <HHTablePagination
                  page={page}
                  component="div"
                  count={currentTabInvoices.length}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(event, newPage) => handlePageChange(newPage)}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  onRowsPerPageChange={event => setPagination({ page: INITIAL_PAGE, rowsPerPage: event.target.value })}
                  ActionsComponent={params => <Box display="none" {...params} />}
                />
                <Pagination
                  color="primary"
                  count={Math.ceil(currentTabInvoices.length / rowsPerPage)}
                  page={page + 1}
                  onChange={(event, newPage) => handlePageChange(newPage - 1)}
                />
              </Box>
            )}

            {!isLoading && currentTabInvoices.length === 0 && <HHSectionPlaceholder title={T.NO_INVOICES} />}
          </Box>
        </AccordionDetails>
      </Accordion>
      <InvoiceActionMenu
        anchorEl={anchorEl}
        selectedInvoice={{
          ...selectedInvoice,
          amountCents: selectedInvoice?.totalCents,
        }}
        onClose={handleCloseActionMenu}
        onAfterRefetch={refreshInvoiceList}
        onAfterRegenerate={activatePolling}
      />
      {isOpenCreateInvoice && (
        <CreateInvoice
          isOpen={isOpenCreateInvoice}
          accountId={accountId}
          billingProfileDetails={get(customerDetails, 'accountBilling.billingProfile', {})}
          callback={invalidateAccountTransaction}
          onClose={() => setIsOpenCreateInvoice(false)}
        />
      )}
      <InvoiceDrawer
        open={isOpenInvoicePDF}
        accountName={customerDetails?.accountName}
        onClose={handleCloseInvoiceDrawer}
        invoiceNumber={selectedInvoice?.invoiceNumber}
        invoiceId={selectedInvoice?.id}
        invoiceStatus={selectedInvoice?.status}
        onEditInvoice={handleEditInvoice}
      />
      <CommonDrawer isOpen={isOpenEditInvoiceWOListing} className="common-drawer-container invoice-work-order" onChange={handleEditInvoice}>
        <InvoiceEdit
          isOpen={isOpenEditInvoiceWOListing}
          invoiceNumber={selectedInvoice?.invoiceNumber}
          accountId={accountId}
          accountName={customerDetails?.accountName}
          invoiceDate={selectedInvoice?.invoiceDate}
          invoiceId={selectedInvoice?.id}
          unPostedAmountCents={selectedInvoice?.totalCents}
          invoiceStatus={selectedInvoice?.status}
          onInvoiceSideView={noop}
          onClose={handleEditInvoice}
          onRefreshTable={invalidateAccountTransaction}
        />
      </CommonDrawer>
      {isOpenTakePaymentsDialog && (
        <TakePaymentsDialog
          isOpen={isOpenTakePaymentsDialog}
          initialAccount={{ id: get(selectedRow, 'accountId'), invoiceNumber: selectedInvoiceNumber }}
          onClose={handlePaymentsDrawerClose}
        />
      )}
    </>
  )
}

Invoices.propTypes = {
  accountId: PropTypes.string.isRequired,
  customerDetails: PropTypes.object,
  initialTab: PropTypes.string,
  showCreateInvoiceButton: PropTypes.bool,
  showARData: PropTypes.bool,
  isViewOnly: PropTypes.bool,
}

export default Invoices
